import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for managing dropdown state and positioning
 * @returns {Object} Object containing dropdown state, refs, and handlers
 */
export const useDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);

    const toggle = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    const close = useCallback(() => {
        setIsOpen(false);
    }, []);

    const open = useCallback(() => {
        setIsOpen(true);
    }, []);

    // Handle click outside
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            // Check if click is outside both trigger and dropdown
            if (
                triggerRef.current &&
                dropdownRef.current &&
                !triggerRef.current.contains(event.target) &&
                !dropdownRef.current.contains(event.target)
            ) {
                close();
            }
        };

        // Use capture phase to catch clicks before they bubble
        document.addEventListener('mousedown', handleClickOutside, true);
        document.addEventListener('touchstart', handleClickOutside, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
            document.removeEventListener('touchstart', handleClickOutside, true);
        };
    }, [isOpen, close]);

    // Position dropdown under trigger button
    useEffect(() => {
        if (!isOpen || !triggerRef.current || !dropdownRef.current) return;

        const updatePosition = () => {
            const trigger = triggerRef.current;
            const dropdown = dropdownRef.current;

            if (!trigger || !dropdown) return;

            // Use viewport-based positioning and then translate back into the
            // coordinate system of the dropdown's offsetParent. This lets us
            // clamp the dropdown so it never leaves the screen horizontally.
            const triggerRect = trigger.getBoundingClientRect();
            const containerRect = dropdown.offsetParent
                ? dropdown.offsetParent.getBoundingClientRect()
                : { left: 0, top: 0 };

            const triggerWidth = triggerRect.width;
            const triggerHeight = triggerRect.height;

            // Get dropdown width (use scrollWidth as fallback if offsetWidth is 0)
            const dropdownWidth = dropdown.offsetWidth || dropdown.scrollWidth || 200; // fallback to 200px

            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            const horizontalMargin = 8; // small margin from viewport edges

            // Desired left position in viewport coordinates (centered under trigger)
            let desiredLeft = triggerRect.left + (triggerWidth / 2) - (dropdownWidth / 2);

            // Clamp so the dropdown always stays within the viewport width
            if (desiredLeft < horizontalMargin) {
                desiredLeft = horizontalMargin;
            } else if (desiredLeft + dropdownWidth > viewportWidth - horizontalMargin) {
                desiredLeft = viewportWidth - horizontalMargin - dropdownWidth;
            }

            // Compute top so the dropdown sits just under the trigger
            const desiredTop = triggerRect.bottom + 8; // 8px vertical gap

            // Convert back from viewport coordinates to offsetParent coordinates
            const finalLeft = desiredLeft - containerRect.left;
            const finalTop = desiredTop - containerRect.top;

            dropdown.style.position = 'absolute';
            dropdown.style.top = `${finalTop}px`;
            dropdown.style.left = `${finalLeft}px`;
            dropdown.style.transform = 'none'; // Remove any existing transform
        };

        // Update position when dropdown opens (with small delay for render)
        const timeoutId = setTimeout(updatePosition, 0);

        // Update position on scroll and resize
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        // Observe dropdown size changes to recalculate position
        const resizeObserver = new ResizeObserver(() => {
            updatePosition();
        });
        
        if (dropdownRef.current) {
            resizeObserver.observe(dropdownRef.current);
        }

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
            resizeObserver.disconnect();
        };
    }, [isOpen]);

    return {
        isOpen,
        toggle,
        open,
        close,
        triggerRef,
        dropdownRef
    };
};

